import * as THREE from 'three';

/** xr-standard thumbstick axes. */
export const AXIS_STICK_X = 2;
export const AXIS_STICK_Y = 3;

export type StickRepeatOptions = {
  /** Deflection past which the stick counts as pushed. */
  threshold?: number;
  /** Delay before a held stick starts repeating, seconds. */
  delaySeconds?: number;
  /** Interval between repeats once started, seconds. */
  intervalSeconds?: number;
  /** Hold time at which acceleration begins / reaches full, seconds. */
  accelStartSeconds?: number;
  accelFullSeconds?: number;
  /** Step multiplier at full acceleration. */
  accelMax?: number;
};

/**
 * Thumbstick-as-D-pad, for nudging a value in discrete steps: a tap is one fine
 * step, a hold repeats and ACCELERATES so a long adjustment doesn't take all
 * day, and each axis is tracked independently.
 *
 * This is the interaction `XRSwingControls.#updateTuneStick` established for the
 * club refinement — where it has proven itself, so it is worth having once
 * rather than hand-rolled per feature. Extracted additively, like
 * `PanelSurface`: swingControls keeps its own copy until this has earned the
 * migration.
 *
 * Reports steps rather than applying them, so the caller decides what an axis
 * means and stays free to remap it — which is the whole point, since the same
 * stick drives different pairs of axes in different stages.
 */
export class StickRepeat {
  #threshold: number;
  #delay: number;
  #interval: number;
  #accelStart: number;
  #accelFull: number;
  #accelMax: number;

  #direction = { x: 0, y: 0 };
  #timer = { x: 0, y: 0 };
  #heldFor = { x: 0, y: 0 };

  constructor(options: StickRepeatOptions = {}) {
    this.#threshold = options.threshold ?? 0.6;
    this.#delay = options.delaySeconds ?? 0.3;
    this.#interval = options.intervalSeconds ?? 0.08;
    this.#accelStart = options.accelStartSeconds ?? 1.0;
    this.#accelFull = options.accelFullSeconds ?? 2.5;
    this.#accelMax = options.accelMax ?? 4;
  }

  /**
   * Sample the stick and return this frame's steps, or 0 per axis for nothing.
   * The magnitude carries the acceleration factor and the sign the direction, so
   * a caller multiplies by its own step size and is done.
   *
   * Note the raw xr-standard sign convention is preserved: stick UP is negative
   * Y. Callers that want up to mean "increase" negate it themselves, because
   * which way is up depends entirely on what is being adjusted.
   */
  sample(dt: number, axes: readonly number[] | undefined): { x: number; y: number } {
    const steps = { x: 0, y: 0 };
    const deflection = (value: number | undefined) =>
      Math.abs(value ?? 0) > this.#threshold ? Math.sign(value!) : 0;
    const wanted = {
      x: deflection(axes?.[AXIS_STICK_X]),
      y: deflection(axes?.[AXIS_STICK_Y]),
    };

    for (const axis of ['x', 'y'] as const) {
      const direction = wanted[axis];
      if (direction === 0) {
        this.#direction[axis] = 0;
        this.#timer[axis] = 0;
        this.#heldFor[axis] = 0;
        continue;
      }

      let fire = false;
      if (direction !== this.#direction[axis]) {
        // Fresh push, or a reversal: one step immediately, then wait out the
        // delay before repeating
        this.#direction[axis] = direction;
        this.#timer[axis] = this.#delay;
        this.#heldFor[axis] = 0;
        fire = true;
      } else {
        this.#heldFor[axis] += dt;
        this.#timer[axis] -= dt;
        if (this.#timer[axis] <= 0) {
          this.#timer[axis] = this.#interval;
          fire = true;
        }
      }
      if (!fire) continue;

      const accel = 1 + (this.#accelMax - 1) * THREE.MathUtils.clamp(
        (this.#heldFor[axis] - this.#accelStart) / (this.#accelFull - this.#accelStart),
        0,
        1,
      );
      steps[axis] = direction * accel;
    }

    return steps;
  }

  /**
   * Forget the held state. Call when the meaning of the axes changes — carrying
   * a live repeat across would fire a step on the new mapping the instant it is
   * selected, before the player has moved anything.
   */
  reset() {
    for (const axis of ['x', 'y'] as const) {
      this.#direction[axis] = 0;
      this.#timer[axis] = 0;
      this.#heldFor[axis] = 0;
    }
  }
}
